import type { Infer } from 'superstruct';
import {
	array,
	boolean,
	dynamic,
	nullable,
	number,
	object,
	optional,
	partial,
	refine,
	string,
} from 'superstruct';

import {
	getFieldParentObject,
	parsableFloat,
	parsableInteger,
	stringDate,
	userString,
	withMessage,
} from '@adflow/shared/util-superstruct';

import {
	AddFacebookAdSetTemplateDto,
	BudgetAmountCoercion,
	ViewFacebookAdSetTemplateDto,
} from './facebook-adset-template.dto';

export const AddFacebookCampaignTemplateDto = object({
	id: optional(parsableInteger()),
	name: userString('validations:Required'),
	objective: userString('validations:Required'),
	status: userString('validations:Required'),
	specialAdCategory: userString('validations:Required'),
	useProductCatalog: optional(boolean()),
	// Advantage+ Campaign Budget fields
	budgetStrategy: optional(string()), // 'CAMPAIGN_BUDGET' | 'ADSET_BUDGET'
	budgetType: dynamic((value, context) => {
		const campaignTemplate = getFieldParentObject(context.branch);
		if (campaignTemplate?.budgetStrategy !== 'CAMPAIGN_BUDGET') {
			return optional(nullable(string()));
		}
		return withMessage(string(), 'validations:Required');
	}), // 'DAILY_BUDGET' | 'LIFETIME_BUDGET'
	budgetAmount: dynamic((value, context) => {
		const campaignTemplate = getFieldParentObject(context.branch);
		if (campaignTemplate?.budgetStrategy !== 'CAMPAIGN_BUDGET') {
			return optional(nullable(BudgetAmountCoercion));
		}

		return withMessage(
			refine(BudgetAmountCoercion, 'aboveZero', (v) => v > 0),
			'validations:Required'
		);
	}),
	bidStrategy: optional(nullable(string())),
	bidAmount: dynamic((value, context) => {
		const campaignTemplate = getFieldParentObject(context.branch);
		const bidStrategy = campaignTemplate?.bidStrategy;
		const requiresAmount =
			bidStrategy === 'LOWEST_COST_WITH_BID_CAP' || bidStrategy === 'COST_CAP';
		if (
			campaignTemplate?.budgetStrategy !== 'CAMPAIGN_BUDGET' ||
			!requiresAmount
		) {
			return optional(nullable(BudgetAmountCoercion));
		}
		return withMessage(
			refine(parsableFloat(), 'aboveZero', (v) => v > 0),
			'validations:Required'
		);
	}),
	roasFloor: dynamic((value, context) => {
		const campaignTemplate = getFieldParentObject(context.branch);
		const isMinRoas =
			campaignTemplate?.budgetStrategy === 'CAMPAIGN_BUDGET' &&
			campaignTemplate?.bidStrategy === 'LOWEST_COST_WITH_MIN_ROAS';
		if (!isMinRoas) {
			return optional(nullable(parsableFloat()));
		}
		return withMessage(
			refine(parsableFloat(), 'aboveZero', (v) => v > 0),
			'validations:Required'
		);
	}),

	adSetTemplates: optional(array(AddFacebookAdSetTemplateDto)),
});

export const ViewFacebookCampaignTemplateDto = object({
	id: number(),
	name: string(),
	objective: string(),
	status: string(),
	specialAdCategory: string(),
	useProductCatalog: optional(boolean()),
	// Advantage+ Campaign Budget fields
	budgetStrategy: string(),
	budgetType: nullable(string()),
	budgetAmount: nullable(number()),
	bidStrategy: nullable(string()),
	bidAmount: nullable(number()),
	roasFloor: nullable(number()),
	createdAt: stringDate(),
	updatedAt: stringDate(),

	adSetTemplates: optional(array(ViewFacebookAdSetTemplateDto)),

	campaigns: optional(
		array(
			object({
				id: number(),
				facebookId: string(),
				syncedAt: stringDate(),
			})
		)
	),
});
export type ViewFacebookCampaignTemplateDto = Infer<
	typeof ViewFacebookCampaignTemplateDto
>;
export type AddFacebookCampaignTemplateDto = Infer<
	typeof AddFacebookCampaignTemplateDto
>;
export const EditFacebookCampaignTemplateDto = partial(
	AddFacebookCampaignTemplateDto
);
export type EditFacebookCampaignTemplateDto = Infer<
	typeof EditFacebookCampaignTemplateDto
>;
